import { NavLink } from "react-router";
import { HomeworkIcon, MessagesIcon, MoreIcon, ScheduleIcon } from "./icons.tsx";

/**
 * Нижняя полоса навигации: решение Р2 из `docs/phone-design-decisions.md`.
 *
 * Ниже порога узкой раскладки (`--w-narrow`, 568 px) и на грубом указателе до
 * 76rem сайдбара нет ни у кого: ни развёрнутого, ни рейкой значков. Рейка это
 * пятнадцать безымянных фигур подряд, и читаются они подсказкой при наведении, а
 * на пальце наведения нет вовсе, то есть нет и половины компонента.
 *
 * ⚠️ **Состав ОДИН на обе роли: Расписание, Домашка, Сообщения, Ещё.** Ролевой
 * разницы в полосе нет намеренно, различие целиком уезжает внутрь листа «Ещё».
 * Первые три это то, зачем берут телефон (посмотреть расписание между уроками,
 * заглянуть в домашку, прочитать сообщение), и у ученика те же три это его
 * собственный продукт. Считать, у кого сколько разделов влезает корешком, больше
 * не надо.
 *
 * ⚠️ **Полоса всегда в разметке, а видимость решает CSS.** Ветка по ширине в
 * React означала бы состояние, которое расходится с медиазапросом; здесь же
 * раскладку решает ровно один источник. `display: none` убирает полосу и из
 * дерева доступности, поэтому два `<nav>` с одной подписью друг другу не мешают:
 * на любой ширине виден ровно один.
 *
 * ⚠️ **Счётчик поднимается к тому пункту, за которым спрятан.** Домашка и
 * сообщения видны сами, поэтому числа стоят на них. «Что нового» уехало за
 * «Ещё», и его число показывает «Ещё»: иначе непрочитанное становится невидимым
 * ровно тогда, когда экран узкий. Когда за «Ещё» заведётся второй счётчик, они
 * складываются, а не встают рядом двумя кружками.
 */
export function TabBar(props: {
  /** Сколько работ ученику задали и он их не открывал. */
  homework: number;
  /** Сколько новых сообщений у человека. */
  messages: number;
  /** Сколько записей «Что нового» он не читал: показывает «Ещё». */
  whatsNew: number;
  /** Сколько карточек показать сегодня: пункт «Карточки» тоже за «Ещё», числа складываются. */
  flashcards: number;
  onMore: () => void;
}) {
  return (
    /*
      Подпись та же, что у сайдбара, и это не копипаста: это одна и та же
      навигация в двух раскладках, а видна из них всегда одна.
    */
    <nav className="tabbar" aria-label="Разделы">
      <Tab to="/schedule" label="Расписание" icon={<ScheduleIcon />} />
      <Tab to="/homework" label="Домашка" icon={<HomeworkIcon />} badge={props.homework} />
      <Tab
        to="/announcements"
        label="Сообщения"
        icon={<MessagesIcon />}
        badge={props.messages}
      />
      {/*
        «Ещё» это кнопка, а не адрес: лист открывается поверх экрана, на котором
        человек стоит, и своего адреса ему не нужно ровно по той же причине, по
        какой его нет у смены пароля. Запись в истории означала бы, что «назад»
        из листа возвращает не на прошлый экран, а на тот же самый с закрытым
        листом.
      */}
      <Tab label="Ещё" icon={<MoreIcon />} badge={props.whatsNew + props.flashcards} onClick={props.onMore} />
    </nav>
  );
}

/**
 * Пункт полосы: значок столбиком над словом.
 *
 * Ссылка и кнопка нарисованы ОДНОЙ функцией, а не двумя ветками разметки: у трёх
 * пунктов из четырёх это `NavLink` с адресом, у «Ещё» кнопка, и две копии одной
 * разметки разъехались бы молча (тестов на компоненты в проекте нет, раздел 9
 * CLAUDE.md).
 *
 * `aria-current="page"` ставит сам `NavLink`, и подсветка висит на атрибуте, а
 * не на своём классе: так «я здесь» видят и глаз, и читалка, и оно не может
 * разойтись с адресом. Ровно тем же живёт пункт сайдбара.
 */
function Tab(props: {
  to?: string;
  label: string;
  icon: React.ReactNode;
  /** Число у пункта. Ноль означает, что метки нет вовсе, а не «0» кружком. */
  badge?: number;
  onClick?: () => void;
}) {
  const badge = props.badge ?? 0;
  /*
    Число уезжает и в `title`, как у пункта сайдбара: кружок с цифрой не говорит,
    о чём он, а слово под значком места для «: 3» не имеет.
  */
  const title = badge === 0 ? props.label : `${props.label}: ${badge}`;
  const guts = (
    <>
      <span className="tab__ico">
        {props.icon}
        {badge > 0 && <span className="badge tab__b">{badge}</span>}
      </span>
      <span className="tab__w">{props.label}</span>
    </>
  );

  if (props.to === undefined) {
    return (
      <button type="button" className="tab" title={title} onClick={props.onClick}>
        {guts}
      </button>
    );
  }
  return (
    <NavLink className="tab" to={props.to} title={title}>
      {guts}
    </NavLink>
  );
}
